import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Card,
    BlockStack,
    InlineStack,
    Text,
    Button,
    Badge,
    Icon,
    Select,
    Modal,
    SkeletonBodyText,
} from '@shopify/polaris';
import { EmailIcon, GlobeIcon, RefreshIcon, XCircleIcon } from '@shopify/polaris-icons';
import {
    reqGetJumpGoogleapisAuth,
    reqGetGoogleAuthTimer,
    reqDisconnectGoogleConsoleStatus,
    reqGetGscBindState,
    reqSelectGscSite,
} from '../../../../api';
import Language from '../../../../i18n/Language';

const t = Language;

const GUIDE_URL = 'https://www.seoant.com/blog/all/how-to-connect-seoant-to-google-search-console';

// Google 多彩 "G" 图标
const GoogleGIcon = () => (
    <svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M23.52 12.273c0-.851-.076-1.67-.218-2.455H12v4.642h6.458a5.52 5.52 0 0 1-2.394 3.622v3.01h3.878c2.269-2.09 3.578-5.166 3.578-8.819Z" fill="#4285F4" />
        <path d="M12 24c3.24 0 5.956-1.075 7.942-2.908l-3.878-3.01c-1.075.72-2.45 1.145-4.064 1.145-3.125 0-5.77-2.11-6.714-4.948H1.276v3.11A11.997 11.997 0 0 0 12 24Z" fill="#34A853" />
        <path d="M5.286 14.279A7.212 7.212 0 0 1 4.91 12c0-.79.136-1.559.376-2.279v-3.11H1.276A11.997 11.997 0 0 0 0 12c0 1.936.464 3.769 1.276 5.389l4.01-3.11Z" fill="#FBBC05" />
        <path d="M12 4.773c1.762 0 3.344.606 4.589 1.795l3.442-3.442C17.951 1.19 15.235 0 12 0A11.997 11.997 0 0 0 1.276 6.611l4.01 3.11C6.23 6.883 8.875 4.773 12 4.773Z" fill="#EA4335" />
    </svg>
);

// 断开连接图标
const UnlinkIcon = () => (
    <svg width="100%" height="100%" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M11.5 6.5 13 5a3.182 3.182 0 0 1 4.5 4.5L16 11M8.5 13.5 7 15a3.182 3.182 0 0 1-4.5-4.5L4 9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
        <path d="m8 8 4 4M15 3l2-2M3 17l-2 2M17 8h2M1 12h2M8 17v2M12 1v2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

// 断开后失去的能力
const DISCONNECT_LOSS_LIST = [
    'Receive AI-powered SEO recommendations based on real Search Console data.',
    'Continuously optimize your pages using indexing and traffic insights',
    "Monitor your pages' indexing and search performance",
    'View Google Search Console reports and analytics.',
];

// 格式化同步时间
const formatSyncTime = (val) => {
    if (val === undefined || val === null || val === '') return '-';
    const raw = `${val}`;
    const num = Number(val);
    if (!isNaN(num) && (raw.length === 10 || raw.length === 13)) {
        const ts = raw.length === 10 ? num * 1000 : num;
        const diff = Date.now() - ts;
        if (diff < 0) return raw;
        const mins = Math.floor(diff / 60000);
        if (mins < 1) return t('Just now');
        if (mins < 60) return t('{{v1}} minutes ago', mins);
        const hours = Math.floor(mins / 60);
        if (hours < 24) return t('{{v1}} hours ago', hours);
        const days = Math.floor(hours / 24);
        return t('{{v1}} days ago', days);
    }
    return raw;
};

function GoogleSearchConsole(props) {
    const { toggleToast, setGscSaveBar } = props;

    const [loading, setLoading] = useState(true);
    const [connected, setConnected] = useState(false);
    const [connectLoading, setConnectLoading] = useState(false);

    const [googleEmail, setGoogleEmail] = useState('');
    const [websiteOptions, setWebsiteOptions] = useState([]);
    const [selectedWebsite, setSelectedWebsite] = useState('');  // 已保存生效的域名
    const [pendingWebsite, setPendingWebsite] = useState('');    // 下拉当前选择（未保存）
    const [lastSync, setLastSync] = useState('');
    const [switchingDomain, setSwitchingDomain] = useState(false);

    const [disconnectOpen, setDisconnectOpen] = useState(false);
    const [disconnectLoading, setDisconnectLoading] = useState(false);

    // gsc 绑定结果弹窗类型（noPropertiesFound / permission / fail）
    const [gscModalType, setGscModalType] = useState(null);

    const timerRef = useRef(null);
    const windowRef = useRef(null);

    const notify = useCallback((msg, isError = false) => {
        if (typeof toggleToast === 'function') {
            toggleToast(msg, isError);
        }
    }, [toggleToast]);

    const applyStatus = useCallback((res) => {
        const data = (res && res.data) || {};
        const list = Array.isArray(data.candidates) ? data.candidates : [];
        // state 1:成功 2:权限不足 3:失败；有当前站点才视为已连接
        const isConnected = res && Number(res.code) === 200 && Number(data.state) === 1;

        if (isConnected) {
            const current = data.website;
            const options = list.length > 0 ? list : [current];
            setConnected(true);
            setWebsiteOptions(options);
            setSelectedWebsite(current);
            setPendingWebsite(current);
            setGoogleEmail(data.email || '');
            setLastSync(data.sync_synced_at || '');
        } else {
            setConnected(false);
        }
    }, []);

    const getStatus = useCallback(async () => {
        try {
            const res = await reqGetGscBindState();
            applyStatus(res);
        } catch (e) {
            setConnected(false);
        } finally {
            setLoading(false);
        }
    }, [applyStatus]);

    // 授权流程返回后，拉取 GSC 绑定结果并按 state 展示对应弹窗
    // state 1:成功 2:权限不足 4:无可用资源；其余视为链接失败（与 OnPageSEO 保持一致）
    const getGscBindState = useCallback(async () => {
        try {
            const res = await reqGetGscBindState();
            const data = (res && res.data) || {};
            const state = Number(data.state);
            if (res && Number(res.code) === 200 && state === 1) {
                applyStatus(res);
                notify(t('Success'), false);
            } else if (state === 2) {
                setGscModalType('permission');
            } else if (state === 4) {
                setGscModalType('noPropertiesFound');
            } else {
                setGscModalType('fail');
            }
        } catch (e) {
            setGscModalType('fail');
        }
    }, [applyStatus, notify]);

    // 隐藏顶部保存栏（空动作）
    const hideSaveBar = useCallback(() => {
        if (typeof setGscSaveBar === 'function') {
            setGscSaveBar(false, { onSave: () => Promise.resolve(true), onDiscard: () => { } });
        }
    }, [setGscSaveBar]);

    useEffect(() => {
        getStatus();
        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
                timerRef.current = null;
            }
            hideSaveBar();
        };
    }, [getStatus, hideSaveBar]);

    // 轮询授权弹窗状态
    const pollAuthStatus = useCallback(async () => {
        if (!windowRef.current || !windowRef.current.closed) return;
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
        setConnectLoading(false);
        try {
            const res = await reqGetGoogleAuthTimer();
            if (res && res.data && res.data.status === 1) {
                await getGscBindState();
            }
        } catch (e) { /* ignore */ }
    }, [getGscBindState]);

    // 连接 Google Search Console
    const handleConnect = useCallback(() => {
        setConnectLoading(true);
        (async () => {
            try {
                const res = await reqGetJumpGoogleapisAuth();
                if (res && res.ack === 1) {
                    const iWidth = 650;
                    const iHeight = 700;
                    const iTop = (window.screen.availHeight - 30 - iHeight) / 2;
                    const iLeft = (window.screen.availWidth - 10 - iWidth) / 2;
                    windowRef.current = window.open(
                        res.data.jump_url,
                        '',
                        `height=${iHeight},innerHeight=${iHeight},width=${iWidth},innerWidth=${iWidth},top=${iTop},left=${iLeft},status=no,toolbar=no,menubar=no,location=no,resizable=no,scrollbars=0,titlebar=no`
                    );
                    timerRef.current = setInterval(pollAuthStatus, 2000);
                } else {
                    setConnectLoading(false);
                    notify(res?.msg || t('Try again later'), true);
                }
            } catch (e) {
                setConnectLoading(false);
                notify(t('Try again later'), true);
            }
        })();
    }, [pollAuthStatus, notify]);

    // 关闭 GSC 绑定结果弹窗
    const closeGscModal = useCallback(() => {
        setGscModalType(null);
    }, []);

    // 重新授权（权限不足 / 链接失败 / 无可用资源）
    const handleGscReauth = useCallback(() => {
        setGscModalType(null);
        handleConnect();
    }, [handleConnect]);

    // 保存生效域名（顶部保存栏点击"保存"后调用），返回是否成功
    const applyDomain = useCallback((value) => {
        setSwitchingDomain(true);
        return reqSelectGscSite({ website: value })
            .then((res) => {
                if (res && (Number(res.code) === 200 || res.ack === 1)) {
                    setSelectedWebsite(value);
                    notify(t('Success'), false);
                    return true;
                }
                notify(res?.msg || t('Try again later'), true);
                return false;
            })
            .catch(() => {
                notify(t('Try again later'), true);
                return false;
            })
            .finally(() => {
                setSwitchingDomain(false);
            });
    }, [notify]);

    // 选择域名：不直接更新，改为唤起顶部 Shopify 保存栏，确认后才更新
    const handleSelectDomain = useCallback((value) => {
        setPendingWebsite(value);
        if (typeof setGscSaveBar !== 'function') return;
        if (value !== selectedWebsite) {
            setGscSaveBar(true, {
                onSave: () => applyDomain(value),
                onDiscard: () => setPendingWebsite(selectedWebsite),
            });
        } else {
            hideSaveBar();
        }
    }, [selectedWebsite, setGscSaveBar, applyDomain, hideSaveBar]);

    // 断开连接
    const handleDisconnect = useCallback(() => {
        setDisconnectLoading(true);
        (async () => {
            try {
                const res = await reqDisconnectGoogleConsoleStatus({});
                if (res && res.code) {
                    setConnected(false);
                    setDisconnectOpen(false);
                    setGoogleEmail('');
                    setWebsiteOptions([]);
                    setSelectedWebsite('');
                    setPendingWebsite('');
                    setLastSync('');
                    hideSaveBar();
                    notify(t('Success'), false);
                } else {
                    notify(res?.msg || t('Try again later'), true);
                }
            } catch (e) {
                notify(t('Try again later'), true);
            } finally {
                setDisconnectLoading(false);
            }
        })();
    }, [notify, hideSaveBar]);

    const domainSelectOptions = websiteOptions.map((web) => ({
        label: web,
        value: web,
    }));

    // 头部：图标 + 标题 + 描述（未连接/已连接共用）
    const headerBlock = (
        <div className="gsc_header">
            <div className="gsc_header_main">
                <div className="gsc_logo">
                    <GoogleGIcon />
                </div>
                <div className="gsc_info">
                    <InlineStack gap="200" blockAlign="center">
                        <Text variant="headingMd" as="h3">{t('Connect Google Search Console')}</Text>
                        {connected && <Badge tone="success">{t('Connected')}</Badge>}
                    </InlineStack>
                    <Text as="p" variant="bodyMd" tone="subdued">
                        {t('Get real traffic data and turn it into actionable SEO insights.')}
                        &nbsp;
                        <a className="gsc_guide" href={GUIDE_URL} target="_blank" rel="noreferrer">{t('View guide.')}</a>
                    </Text>
                </div>
            </div>
            <div className="gsc_header_action">
                {connected ? (
                    <Button
                        tone="critical"
                        // icon={unio}
                        onClick={() => setDisconnectOpen(true)}
                    >
                        <span className='flex_layout' style={{ gap: '4px' }}>
                            <span className='flex_layout'>
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M16.4219 13.1055C16.8361 13.1055 17.1719 13.4413 17.1719 13.8555C17.1716 14.2694 16.8359 14.6045 16.4219 14.6045H14.6035V16.4238C14.6035 16.838 14.2677 17.1738 13.8535 17.1738C13.4393 17.1738 13.1035 16.838 13.1035 16.4238V13.8555C13.1035 13.4413 13.4393 13.1055 13.8535 13.1055H16.4219ZM5.61621 8.18359C5.90899 7.89091 6.38387 7.8911 6.67676 8.18359C6.96948 8.4765 6.96959 8.95228 6.67676 9.24512L5.39258 10.5293C4.26699 11.6552 4.26681 13.4807 5.39258 14.6064C6.51837 15.7319 8.34394 15.7319 9.46973 14.6064L10.7539 13.3213C11.0467 13.0286 11.5216 13.0287 11.8145 13.3213C12.1073 13.6142 12.1073 14.0889 11.8145 14.3818L10.5303 15.667C8.8187 17.3783 6.04361 17.3783 4.33203 15.667C2.62048 13.9554 2.62066 11.1804 4.33203 9.46875L5.61621 8.18359ZM11.3965 7.54199C11.6894 7.2491 12.1641 7.2491 12.457 7.54199C12.7499 7.83489 12.7499 8.30965 12.457 8.60254L8.60352 12.4561C8.3106 12.7486 7.83575 12.7488 7.54297 12.4561C7.25019 12.1633 7.25041 11.6884 7.54297 11.3955L11.3965 7.54199ZM9.46973 4.33301C11.1813 2.62173 13.9564 2.62173 15.668 4.33301C17.3795 6.04456 17.3793 8.81959 15.668 10.5312L14.3838 11.8164C14.091 12.1091 13.6161 12.1089 13.3232 11.8164C13.0305 11.5235 13.0304 11.0477 13.3232 10.7549L14.6074 9.4707C15.733 8.34483 15.7332 6.51932 14.6074 5.39355C13.4816 4.26806 11.6561 4.26806 10.5303 5.39355L9.24609 6.67871C8.95327 6.97143 8.47843 6.97133 8.18555 6.67871C7.89268 6.38585 7.89275 5.91106 8.18555 5.61816L9.46973 4.33301ZM6.14648 2.82812C6.56067 2.82816 6.89648 3.16393 6.89648 3.57812V6.14648C6.89648 6.56068 6.56067 6.89645 6.14648 6.89648H3.57715C3.16316 6.89622 2.82812 6.56054 2.82813 6.14648C2.82812 5.73227 3.16391 5.39648 3.57812 5.39648H5.39648V3.57812C5.39648 3.16394 5.73231 2.82817 6.14648 2.82812Z" fill="#8E1F0B" />
                                </svg>
                            </span>
                            {t('Disconnect')}
                        </span>
                    </Button>
                ) : (
                    <Button variant="primary" loading={connectLoading} onClick={handleConnect}>
                        {t('Connect now')}
                    </Button>
                )}
            </div>
        </div>
    );

    // 已连接：详情行
    const detailBlock = (
        <div className="gsc_body">
            <div className="gsc_detail_row">
                <div className="gsc_detail_label">
                    <Icon source={EmailIcon} tone="subdued" />
                    <Text as="span" variant="bodyMd" tone="subdued">{t('Google Account')}</Text>
                </div>
                <Text as="span" variant="bodyMd">{googleEmail || '-'}</Text>
            </div>
            <div className="gsc_detail_row">
                <div className="gsc_detail_label">
                    <Icon source={GlobeIcon} tone="subdued" />
                    <Text as="span" variant="bodyMd" tone="subdued">{t('Select Domain (Property)')}</Text>
                </div>
                <div className="gsc_domain_select">
                    <Select
                        label={t('Select Domain (Property)')}
                        labelHidden
                        options={domainSelectOptions}
                        value={pendingWebsite}
                        onChange={handleSelectDomain}
                        disabled={switchingDomain || domainSelectOptions.length === 0}
                    />
                </div>
            </div>
            <div className="gsc_detail_row">
                <div className="gsc_detail_label">
                    <Icon source={RefreshIcon} tone="subdued" />
                    <Text as="span" variant="bodyMd" tone="subdued">{t('Data Sync')}</Text>
                </div>
                <Text as="span" variant="bodyMd">{formatSyncTime(lastSync)}</Text>
            </div>
        </div>
    );

    // gsc 绑定结果弹窗（无可用资源 / 权限不足 / 链接失败），与 OnPageSEO 状态判断保持一致
    const gscModalConfigMap = {
        noPropertiesFound: {
            title: t('No Search Console Properties Found'),
            icon: '/imgs/gsc/connect_fail.png',
            desc: t("We couldn't find any Search Console properties in this Google account. Please try another account."),
            subDesc: t("If you haven't added your website to Google Search Console yet, please create and verify a property first."),
        },
        permission: {
            title: t('Additional Permission Required'),
            icon: '/imgs/gsc/connect_permission.png',
            desc: t('We need read-only access to Google Search Console to load your data. Please authorize again and grant all requested permissions on the Google consent screen.'),
        },
        fail: {
            title: t('Unable to connect to Google Search Console'),
            icon: '/imgs/gsc/connect_fail.png',
            desc: t("We couldn't connect to Google Search Console right now due to a temporary network issue or API rate limiting. Please try again in a few minutes."),
        },
    };
    const gscModalCfg = gscModalType ? gscModalConfigMap[gscModalType] : null;

    if (loading) {
        return (
            <Card>
                <BlockStack gap="300">
                    <SkeletonBodyText />
                </BlockStack>
            </Card>
        );
    }

    return (
        <div className="gsc_integration">
            <Card padding="0">
                <div className={`gsc_card${connected ? ' gsc_card--connected' : ''}`}>
                    {headerBlock}
                    {connected && detailBlock}
                </div>
            </Card>

            <Modal
                open={disconnectOpen}
                onClose={() => setDisconnectOpen(false)}
                title={t('Are you sure you want to disconnect?')}
                primaryAction={{
                    content: t('Confirm'),
                    onAction: handleDisconnect,
                    loading: disconnectLoading,
                }}
                secondaryActions={[{
                    content: t('Cancel'),
                    onAction: () => setDisconnectOpen(false),
                    disabled: disconnectLoading,
                }]}
            >
                <Modal.Section>
                    <BlockStack gap="200">
                        <Text as="p" variant="headingSm">{t('You will no longer be able to')}:</Text>
                        <div className="gsc_modal_list">
                            {DISCONNECT_LOSS_LIST.map((item) => (
                                <div className="gsc_modal_item" key={item}>
                                    <span className="gsc_modal_item_icon">
                                        <Icon source={XCircleIcon} tone="critical" />
                                    </span>
                                    <Text as="span" variant="bodyMd" tone="subdued">{t(item)}</Text>
                                </div>
                            ))}
                        </div>
                    </BlockStack>
                </Modal.Section>
            </Modal>

            {gscModalCfg && (
                <Modal
                    size="small"
                    open={Boolean(gscModalType)}
                    onClose={closeGscModal}
                    title={gscModalCfg.title}
                    primaryAction={{ content: t('Authorize again'), onAction: handleGscReauth }}
                    secondaryActions={[{ content: t('Cancel'), onAction: closeGscModal }]}
                >
                    <Modal.Section>
                        <div className="gsc_bind_modal">
                            <div className="gsc_bind_modal_icon">
                                <img src={gscModalCfg.icon} alt="" />
                            </div>
                            <div>
                                <p className="gsc_bind_modal_desc">{gscModalCfg.desc}</p>
                                {gscModalCfg.subDesc && (
                                    <p className="gsc_bind_modal_subdesc">
                                        {gscModalCfg.subDesc}{' '}
                                        <span onClick={() => {
                                            window.open('https://www.seoant.com/how-to-connect-seoant-to-google-search-console/', '_blank');
                                        }}>{t('Learn how.')}</span>
                                    </p>
                                )}
                            </div>
                        </div>
                    </Modal.Section>
                </Modal>
            )}
        </div>
    );
}

export default GoogleSearchConsole;
